import { create } from 'zustand';

import { assetSortTimeMs, type AssetWithJobContext } from '@/types';

/**
 * R29 + R31 — Per-project asset history store(對齊 docs/22)
 * -----------------------------------------------------------------------------
 * 解 user 痛點:reload 後不再丟失歷史 asset。Canvas mount 拉一次完整列表,跨 job 持
 * 久顯示在 ToolInspector「成果」tab。
 *
 * **不持久化進 localStorage**:
 *   - signed URL 走 docs/21 endpoint 取(每次 fresh)— 沒理由把 metadata cache 進 disk
 *   - localStorage 5MB 上限,asset 累積太多會炸
 *   - 對齊 docs/22 §4.4 設計:reload 後 fetch 一次拿真實 truth,不依賴 stale localStorage
 *
 * **R31 衛生補強(panel DB Optimizer)**:
 *   - **MAX_ASSETS_PER_PROJECT cap**:long-lived tab + 100+ asset 場景無界增長 → 截斷
 *     最舊條目(by createdDatetime desc 已排序,直接 slice 前 N 筆即可)
 *   - **TTL 30min on lastSeen**:每次 setAssets / upsertAssets / appendAssets 觸發
 *     `touchLastSeen`;`pruneStale` 主動丟超 TTL 的條目;F2 onSnapshot 進場前先把
 *     unbounded growth 釘死
 *   - sort 仍走原 O(n log n) — n ≤ 200,實測無壓力,binary insert 留給更大 dataset
 *
 * **跟 useActiveJobStore 分流**:
 *   - useActiveJobStore:當前進行中 / 最新 active job(canvas lock + JobProgressInspector 顯)
 *   - useProjectAssetsStore(本檔):**所有歷史** asset(NodeAssetPreview 跨 job 顯)
 *
 * 兩者並存:user 跑新 job 時 activeJob 更新,assetsByProject 也增量(canvas mount 拉一次,
 * job 跑完後可 manual refetch — 對齊 docs/22 §4.4)。
 */

/** R31:每 project 最多保留多少 asset(LRU evict 最舊 by createdDatetime)。 */
export const MAX_ASSETS_PER_PROJECT = 200;

/** R31:每 asset 的 TTL(ms);超過 lastSeen + TTL 視為 stale,可被 pruneStale 丟掉。 */
export const ASSET_TTL_MS = 30 * 60 * 1000; // 30 min

export interface ProjectAssetsState {
  /** key = projectId,value = 該 project 累積 assets(by createdDatetime desc) */
  assetsByProject: Record<string, AssetWithJobContext[]>;
  /** key = projectId,value = 上一次 list 回的 nextCursor(null = 沒下一頁) */
  cursorsByProject: Record<string, string | null>;
  /** key = projectId,value = 是否正在 fetch(canvas mount / fetchMore 用) */
  loadingByProject: Record<string, boolean>;
  /**
   * R31:per-asset last-seen ts(ms,Date.now()),用於 TTL prune。Map 簡單 O(1)
   * lookup;不放進 assetsByProject 條目避免污染 :type:`AssetWithJobContext` 對外契約。
   */
  lastSeenByProject: Record<string, Map<string, number>>;

  /** Canvas mount 拉第一頁 → 整個覆蓋。 */
  setAssets: (
    projectId: string,
    assets: AssetWithJobContext[],
    nextCursor: string | null,
  ) => void;
  /** fetchMore 拉下一頁 → append 到既有 list。 */
  appendAssets: (
    projectId: string,
    assets: AssetWithJobContext[],
    nextCursor: string | null,
  ) => void;
  setLoading: (projectId: string, loading: boolean) => void;
  /** Job submit 後新 assets 進來 → 跟既有 list dedupe by assetId,新者優先。 */
  upsertAssets: (projectId: string, assets: AssetWithJobContext[]) => void;
  /**
   * R31:主動丟 30min TTL 過期條目 — long-lived tab + 100+ asset 場景每隔一段時間
   * 跑一次防 unbounded growth。可由 caller 注入 `now` 給 test 驗。
   */
  pruneStale: (now?: number) => void;
  clearProject: (projectId: string) => void;
  /** onAuthLost 用 — 切 user 時清光所有 project asset cache。 */
  clearAll: () => void;
}

/**
 * R31 helper:對 sorted-desc 的 list 套 cap MAX_ASSETS_PER_PROJECT;同步刪 lastSeen 條目。
 *
 * **為什麼純函式**:多個 set 路徑(setAssets / appendAssets / upsertAssets)都需要 cap,
 * 抽出避免重複邏輯。對應 lastSeen Map 也一併修剪保證一致。
 */
function capAssets(
  assets: AssetWithJobContext[],
  lastSeen: Map<string, number>,
): { assets: AssetWithJobContext[]; lastSeen: Map<string, number> } {
  if (assets.length <= MAX_ASSETS_PER_PROJECT) {
    return { assets, lastSeen };
  }
  const kept = assets.slice(0, MAX_ASSETS_PER_PROJECT);
  const keptIds = new Set(kept.map((a) => a.assetId));
  const nextLastSeen = new Map<string, number>();
  for (const [id, ts] of lastSeen) {
    if (keptIds.has(id)) nextLastSeen.set(id, ts);
  }
  return { assets: kept, lastSeen: nextLastSeen };
}

/**
 * R31 helper:sort assets desc by createdDatetime(對齊 docs/22 §2.1)。
 */
function sortByCreatedDesc(assets: AssetWithJobContext[]): AssetWithJobContext[] {
  return [...assets].sort((a, b) => {
    // R199 — 排序鍵改走 `assetSortTimeMs`:createdDatetime → assetId ULID → jobId ULID。
    // R59 的「null 視為最新」(MAX_SAFE_INTEGER,不可當 0 否則 LRU cap 誤 evict 剛產出的)
    // 保留在最後一層 —— 但現在只有 id 不是 ULID 才會走到。
    const ta = assetSortTimeMs(a) ?? Number.MAX_SAFE_INTEGER;
    const tb = assetSortTimeMs(b) ?? Number.MAX_SAFE_INTEGER;
    if (ta !== tb) return tb - ta;
    // 同毫秒(或兩者都無時間)→ ULID 字典序即時間序,給一個**決定性**的順序;
    // 沒有這層時 sort 穩定性會讓順序退化成「進 store 的先後」(R199 版本標籤反轉的一環)。
    return b.assetId.localeCompare(a.assetId);
  });
}

/**
 * R31 helper:把 list 內所有 asset 在 lastSeen Map 蓋上 now ts。
 */
function touchAll(
  ids: AssetWithJobContext[],
  lastSeen: Map<string, number>,
  now: number,
): Map<string, number> {
  const next = new Map(lastSeen);
  for (const a of ids) next.set(a.assetId, now);
  return next;
}

export const useProjectAssetsStore = create<ProjectAssetsState>()((set) => ({
  assetsByProject: {},
  cursorsByProject: {},
  loadingByProject: {},
  lastSeenByProject: {},

  setAssets: (projectId, assets, nextCursor) =>
    set((state) => {
      const now = Date.now();
      const sorted = sortByCreatedDesc(assets);
      const newLastSeen = touchAll(sorted, new Map(), now);
      const capped = capAssets(sorted, newLastSeen);
      return {
        assetsByProject: { ...state.assetsByProject, [projectId]: capped.assets },
        cursorsByProject: { ...state.cursorsByProject, [projectId]: nextCursor },
        lastSeenByProject: { ...state.lastSeenByProject, [projectId]: capped.lastSeen },
      };
    }),

  appendAssets: (projectId, assets, nextCursor) =>
    set((state) => {
      const now = Date.now();
      const existing = state.assetsByProject[projectId] ?? [];
      const existingLastSeen = state.lastSeenByProject[projectId] ?? new Map();
      // append 不重排,但仍要 cap(已 sorted desc 假設,append 通常是更舊的下一頁)
      const merged = [...existing, ...assets];
      const newLastSeen = touchAll(merged, existingLastSeen, now);
      const capped = capAssets(merged, newLastSeen);
      return {
        assetsByProject: { ...state.assetsByProject, [projectId]: capped.assets },
        cursorsByProject: { ...state.cursorsByProject, [projectId]: nextCursor },
        lastSeenByProject: { ...state.lastSeenByProject, [projectId]: capped.lastSeen },
      };
    }),

  setLoading: (projectId, loading) =>
    set((state) => ({
      loadingByProject: { ...state.loadingByProject, [projectId]: loading },
    })),

  upsertAssets: (projectId, assets) =>
    set((state) => {
      const now = Date.now();
      const existing = state.assetsByProject[projectId] ?? [];
      const existingLastSeen = state.lastSeenByProject[projectId] ?? new Map();
      const byId = new Map<string, AssetWithJobContext>();
      // 後者優先(新 entry 覆蓋舊)
      for (const a of existing) byId.set(a.assetId, a);
      for (const a of assets) byId.set(a.assetId, a);
      const merged = sortByCreatedDesc(Array.from(byId.values()));
      const newLastSeen = touchAll(merged, existingLastSeen, now);
      const capped = capAssets(merged, newLastSeen);
      return {
        assetsByProject: { ...state.assetsByProject, [projectId]: capped.assets },
        lastSeenByProject: { ...state.lastSeenByProject, [projectId]: capped.lastSeen },
      };
    }),

  pruneStale: (nowOverride) =>
    set((state) => {
      const now = nowOverride ?? Date.now();
      const cutoff = now - ASSET_TTL_MS;
      const nextAssets: Record<string, AssetWithJobContext[]> = {};
      const nextLastSeen: Record<string, Map<string, number>> = {};
      let changed = false;
      for (const projectId of Object.keys(state.assetsByProject)) {
        const list = state.assetsByProject[projectId] ?? [];
        const ls = state.lastSeenByProject[projectId] ?? new Map();
        const fresh = list.filter((a) => (ls.get(a.assetId) ?? now) >= cutoff);
        if (fresh.length !== list.length) changed = true;
        const newLs = new Map<string, number>();
        for (const a of fresh) {
          const ts = ls.get(a.assetId);
          if (ts != null) newLs.set(a.assetId, ts);
        }
        nextAssets[projectId] = fresh;
        nextLastSeen[projectId] = newLs;
      }
      if (!changed) return state;
      return {
        assetsByProject: nextAssets,
        lastSeenByProject: nextLastSeen,
      };
    }),

  clearProject: (projectId) =>
    set((state) => {
      if (!(projectId in state.assetsByProject)) return state;
      const nextAssets = { ...state.assetsByProject };
      delete nextAssets[projectId];
      const nextCursors = { ...state.cursorsByProject };
      delete nextCursors[projectId];
      const nextLoading = { ...state.loadingByProject };
      delete nextLoading[projectId];
      const nextLastSeen = { ...state.lastSeenByProject };
      delete nextLastSeen[projectId];
      return {
        assetsByProject: nextAssets,
        cursorsByProject: nextCursors,
        loadingByProject: nextLoading,
        lastSeenByProject: nextLastSeen,
      };
    }),

  clearAll: () =>
    set({
      assetsByProject: {},
      cursorsByProject: {},
      loadingByProject: {},
      lastSeenByProject: {},
    }),
}));

/**
 * Selector hook — 讀某 project + node 的 asset list(已 filter by nodeId)。
 *
 * 不傳 nodeId → 回該 project 所有 asset。
 *
 * R134.2(docs/75 §6.0.1)— 短影片 A/B 卡:BE submit 期把卡展開成隱藏子圖,段產物
 * asset 的 `node_id` 是**決定性衍生 id**(`{nid}::base` / `::ext` / `::seg1` …),
 * 不是畫布上那張 A/B 卡的 nid。此處把 `{nid}::*` 歸屬回主卡,否則 A/B 卡的
 * 「成果」tab 永遠是空的(真實 nid 是 ULID,不含 `::`,一般卡不受影響)。
 */
export function useProjectAssets(
  projectId: string,
  nodeId?: string,
): AssetWithJobContext[] {
  return useProjectAssetsStore((s) => {
    const all = s.assetsByProject[projectId] ?? [];
    if (!nodeId) return all;
    return all.filter((a) => a.nodeId === nodeId || a.nodeId.split('::')[0] === nodeId);
  });
}

/** Selector hook — 是否正在 fetch(canvas mount loading 顯 spinner 用)。 */
export function useProjectAssetsLoading(projectId: string): boolean {
  return useProjectAssetsStore((s) => s.loadingByProject[projectId] ?? false);
}

/**
 * R61 Selector hook — 依 assetId 查單一 asset(含 `assetKind` / `gcsUri`)。
 *
 * 結果藍卡(持久 OUTPUT 節點)只持有 `asset_id`;canvas 端渲染縮圖需 `assetKind`
 * (image / video 走不同 element)。store 由 canvas mount `assetsService.list` +
 * job 進行中 `useJobListener` upsert 填充 → 查得到 kind;查不到(超出 fetch limit /
 * 尚未載入)caller 端 fallback。
 */
export function useAssetById(
  projectId: string,
  assetId: string | undefined,
): AssetWithJobContext | undefined {
  return useProjectAssetsStore((s) =>
    assetId ? (s.assetsByProject[projectId] ?? []).find((a) => a.assetId === assetId) : undefined,
  );
}
